import React, {CSSProperties, DetailedHTMLProps, HTMLAttributes, RefObject} from "react";
import {Map, MapRef, MapProps} from 'react-map-gl/maplibre';
import {
    CameraOptions,
    FlyToOptions,
    JumpToOptions,
    LngLat,
    LngLatBounds,
    MapLayerMouseEvent, Point, type MapLibreEvent, LngLatLike, LngLatBoundsLike,
} from "maplibre-gl";
import {FullscreenControl, GeolocateControl, NavigationControl, ScaleControl, FullscreenControlProps, GeolocateControlProps, NavigationControlProps, ScaleControlProps} from "react-map-gl/maplibre";
import {getManager} from "../../ts/manager/Manager";
import MapCluster, {MapClusterInstance, IMapClusterProps} from "./element/MapCluster";
import MapMarker, {IMarkerProps, MapMarkerInstance} from "./element/MapMarker";
import {IMapMarkerIconProps} from "./element/MapMarkerIcon";
import {isObjectMatch} from "../../ts/common/SiteHelper";
import MapPopup, {IMapPopupProps, MapPopupInstance} from "./element/MapPopup";
import CooldownHandler from "../../ts/common/CooldownHandler";
import {mapCoreConfig} from "./config/MapCoreConfig";
import {CountryCode} from "./location/LocationType";
import {ILocationAddress} from "./location/LocationInterface";
import AppComponent, {IAppComponentProps} from "../../tsx/component/AppComponent";

export type MapFocus = "cluster" | "primary_marker"  | "country" | "user" | "defined" | "default";

export interface MapLibreMapProps extends IAppComponentProps
{
    preferredMapFocus?:MapFocus;
    preferredMapFocusOrder?:MapFocus[];
    countryCode?:CountryCode;
    onMapChange?:() => void;
    onMapInteraction?:() => void;
    onMarkerSelected?:(marker:MapMarkerInstance) => void;
    definedFocus?:LngLatLike;
    definedFocusBounds?:LngLatBoundsLike;
    allowMapContainerFocusScroll?:boolean;

    outerContainer?:DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>
    map?:MapProps|null
    userMarker?:IMarkerProps|null
    primaryMarker?:IMarkerProps|null
    cluster?:IMapClusterProps|null
    popup?:IMapPopupProps|null
    useAssignedUserMarkerPosition?:boolean,
    geolocateControl?:GeolocateControlProps,
    fullscreenControl?:FullscreenControlProps,
    navigationControl?:NavigationControlProps|null,
    scaleControl?:ScaleControlProps
    enableContextMenu?:boolean
    limitFlyToCurve?:number
    mapRef?:RefObject<any>
}

export class MapLibreMapInstance extends AppComponent<MapLibreMapProps>
{
    props: MapLibreMapProps;

    elementID: string = null!;

    preferredMapFocusOrder: MapFocus[] = ["cluster", "primary_marker", "country", "user", "defined", "default"];
    preferredMapFocus: MapFocus = "cluster";

    userLocation: {
        didRequest?: boolean;
        didReceive?: boolean;
        position?: LngLat|null;
        countryCode?: CountryCode|null;
    } = {}

    countryLocation: {
        didRequest?: boolean;
        didReceive?: boolean;
        countryCode?: CountryCode|null;
        bounds?:LngLatBounds|null
    } = {}

    haveAllExternalData: boolean = false;
    isMapReady: boolean = false;

    mapOuterContainerRef = React.createRef<HTMLDivElement>();
    mapRef = React.createRef<MapRef>();
    userMarkerRef = React.createRef<MapMarkerInstance>();
    primaryMarkerRef = React.createRef<MapMarkerInstance>();
    clusterRef = React.createRef<MapClusterInstance>();
    popupRef = React.createRef<MapPopupInstance>();
    previousZoom = 0;
    previousLngLat = new LngLat(0, 0);
    selectedMarker: MapMarkerInstance | null = null;
    showSelectedMarkerPopup: boolean = false;

    outerContainerProps: DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> = null!;
    mapProps:MapProps = null!;
    userMarkerProps:IMarkerProps|null = null;
    primaryMarkerProps:IMarkerProps|null = null;

    definedFocus:LngLat = null!;
    definedFocusBounds:LngLatBounds|null = null;

    didFindActiveFocus:boolean = false;
    didFindInitialFocus: boolean = false;
    mapInteractionCooldown = new CooldownHandler(true);
    mapChangeCooldown = new CooldownHandler(true);

    popupProps:IMapPopupProps|null = null;

    transitionKey:number = 0
    transitionReady:boolean = false

    constructor(props: MapLibreMapProps)
    {
        super(props);
        this.props = props;
    }

    bindFunctions()
    {
        super.bindFunctions();
        this.onMapLoad = this.onMapLoad.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
    }

    applyNextProps(nextProps:MapLibreMapProps, props?:MapLibreMapProps)
    {
        super.applyNextProps(nextProps, props);

        if(this.selectedMarker){
            if((this.selectedMarker !== this.getUserMarker()) && (this.selectedMarker !== this.getPrimaryMarker()) && !(this.getCluster()?.isClusterMarker(this.selectedMarker.markerID, true) ?? false)){
                this.selectMarker(null);
            }
        }


        if(nextProps.userMarker && !this.props.useAssignedUserMarkerPosition && !this.userLocation.didRequest){
            this.requestUserLocation();
        }
        if(nextProps.countryCode && (!this.countryLocation.didRequest || (this.countryLocation.countryCode !== nextProps.countryCode))){
            this.requestCountryLocation(nextProps.countryCode);
        }

        let preferredMapFocusOrder: MapFocus[]|null = null;
        let preferredMapFocus: MapFocus|null = null;

        let outerContainerProps: DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>|null = null;
        let mapProps: MapProps|null = null;
        let userMarkerProps: IMarkerProps|null = null;
        let primaryMarkerProps: IMarkerProps|null = null;

        let definedFocus:LngLat|null = null;
        let definedFocusBounds:LngLatBounds|null = null;

        if(!props){
            this.elementID = nextProps.routeProps.choice.realPath + "__MapLibreMap";
        } else {


            preferredMapFocusOrder = props.preferredMapFocusOrder === nextProps.preferredMapFocusOrder ? this.preferredMapFocusOrder : null;
            preferredMapFocus = props.preferredMapFocus === nextProps.preferredMapFocus ? this.preferredMapFocus : null;

            outerContainerProps = props.outerContainer === nextProps.outerContainer ? this.outerContainerProps : null;
            mapProps = props.map === nextProps.map ? this.mapProps : null;
            userMarkerProps = (props.userMarker === nextProps.userMarker) ? this.userMarkerProps : null;
            primaryMarkerProps = props.primaryMarker === nextProps.primaryMarker ? this.primaryMarkerProps : null;

            definedFocus = props.definedFocus === nextProps.definedFocus ? this.definedFocus : null;
            definedFocusBounds = props.definedFocusBounds === nextProps.definedFocusBounds ? this.definedFocusBounds : null;

            if(
                outerContainerProps &&
                this.isMapReady &&
                !(outerContainerProps.className ?? "").includes(" is-initialized")
            ){
                outerContainerProps = null;
            }

            if(
                userMarkerProps && !this.props.useAssignedUserMarkerPosition &&
                (!this.userLocation.position ||
                    (this.userLocation.position.lat !== userMarkerProps.marker.latitude) ||
                    (this.userLocation.position.lng !== userMarkerProps.marker.longitude)
                )
            )
            {
                userMarkerProps = null;
            }
        }

        if(!preferredMapFocusOrder)
        {
            preferredMapFocusOrder = (nextProps.preferredMapFocusOrder ?? []).length ? nextProps.preferredMapFocusOrder! : (mapCoreConfig?.preferredMapFocusOrder ?? []);
        }

        if(!preferredMapFocus)
        {
            preferredMapFocus = nextProps.preferredMapFocus ?? (preferredMapFocusOrder[0] ?? "default");
        }

        if(!outerContainerProps)
        {
            const style = nextProps.outerContainer?.style ?? {};
            const width100 = !style.width && (!style.height || !style.aspectRatio);
            const useDefaultAspectRatio = !(style.width && !width100) || !style.height;

            const useStyle: CSSProperties =
                {
                    ...style,
                    width: style.width ?? (width100 ? "100%" : undefined),
                    height: style.height,
                    aspectRatio: useDefaultAspectRatio ? 16/9 : undefined
                }
            outerContainerProps = {
                ... nextProps.outerContainer,
                ref:this.mapOuterContainerRef,
                style: useStyle,
                className: " osm-map mb-3 " + (nextProps.outerContainer?.className ?? "") + (this.isMapReady ? " is-initialized" : "")
            };
        }

        const fromMapProps = nextProps.map ?? null;
        const configMapProps = mapCoreConfig.map ?? null;

        const defaultMapZoomLevel = fromMapProps?.initialViewState?.zoom ?? (configMapProps?.initialViewState?.zoom ?? null)


        if(!mapProps)
        {
            const projection = fromMapProps?.projection ?? configMapProps?.projection;
            mapProps = {
                ...configMapProps,
                ...fromMapProps,
                initialViewState: {
                    ...configMapProps?.initialViewState,
                    ...fromMapProps?.initialViewState,
                    latitude: fromMapProps?.initialViewState?.latitude ?? (configMapProps?.initialViewState?.latitude ?? 0),
                    longitude: fromMapProps?.initialViewState?.longitude ?? (configMapProps?.initialViewState?.longitude ?? 0),
                    zoom: defaultMapZoomLevel ?? 0
                },
                attributionControl: fromMapProps?.attributionControl ?? (configMapProps?.attributionControl ?? false),
                mapStyle: fromMapProps?.mapStyle ?? configMapProps?.mapStyle,
                reuseMaps: fromMapProps?.reuseMaps ?? (configMapProps?.reuseMaps ?? false),
                projection: projection,
                dragRotate: fromMapProps?.dragRotate ?? (configMapProps?.dragRotate ?? (!projection ? false : undefined)),
                onClick: (event:MapLayerMouseEvent) => {
                    if(!this.isMapReady){
                        return;
                    }
                    const doubleClickZoom = this.mapProps.doubleClickZoom !== false ? (this.getMap()?.getMap().doubleClickZoom ?? null) : null;
                    if((event.originalEvent as any).clickOnLayer){
                        if(doubleClickZoom){
                            doubleClickZoom.disable();
                        }
                        return;
                    } else {
                        if(doubleClickZoom){
                            doubleClickZoom.enable();
                        }
                    }
                    if(this.showSelectedMarkerPopup && (this.popupProps?.closeOnMapClick ?? (mapCoreConfig.popup?.closeOnMapClick) ?? false)){
                        this.selectMarker(null);
                    }
                    if(this.props.map?.onClick){
                        this.props.map.onClick(event)
                    }
                }
            };
        }
        if(!definedFocus)
        {
            definedFocus = LngLat.convert(this.props.definedFocus ?? (mapCoreConfig.definedFocus ?? [mapProps.initialViewState!.longitude!, mapProps.initialViewState!.latitude!]));
        }

        if(!definedFocusBounds)
        {
            const fromDefinedFocusBounds = this.props.definedFocusBounds ?? (mapCoreConfig.definedFocusBounds ?? null);
            definedFocusBounds = fromDefinedFocusBounds ? LngLatBounds.convert(fromDefinedFocusBounds) : null;
        }

        if(!userMarkerProps && !!nextProps.userMarker && (!!this.userLocation.position || !!this.props.useAssignedUserMarkerPosition))
        {
            const fromProps = nextProps.userMarker;
            const fromConfigProps = mapCoreConfig.userMarker ?? null;
            let iconProps:IMapMarkerIconProps = {
                ...fromConfigProps?.iconProps,
                ...fromProps?.iconProps,
                imagePath: fromProps.iconProps?.imagePath !== undefined ? fromProps.iconProps?.imagePath : fromConfigProps?.iconProps?.imagePath
            }
            iconProps = fromProps?.iconProps && isObjectMatch(fromProps, iconProps) ? fromProps.iconProps  : iconProps;
            let defaultZoomLevel = fromProps.defaultZoomLevel ?? null;
            const fallbackToBoundsZoomLevel= fromProps.fallbackToBoundsZoomLevel ?? (fromConfigProps?.fallbackToBoundsZoomLevel ?? false);
            if(!fallbackToBoundsZoomLevel){
                defaultZoomLevel = defaultZoomLevel ?? (fromConfigProps?.defaultZoomLevel ?? defaultMapZoomLevel);
            }
            userMarkerProps = {
                ...fromProps,
                marker: !!this.userLocation.position && !this.props.useAssignedUserMarkerPosition ? {
                    ...fromProps.marker,
                    latitude: this.userLocation.position.lat,
                    longitude: this.userLocation.position.lng
                } : fromProps.marker,
                iconProps: iconProps,
                defaultZIndex: fromProps?.defaultZIndex ?? fromConfigProps?.defaultZIndex,
                centreOnClick: fromProps?.centreOnClick ?? (fromConfigProps?.centreOnClick ?? false),
                defaultZoomLevel: defaultZoomLevel
            };
            if(fromProps?.popupContent !== null){
                userMarkerProps.popupContent = userMarkerProps.popupContent ?? (fromConfigProps?.popupContent ?? null);
            }

        }

        if(!primaryMarkerProps && nextProps.primaryMarker)
        {
            const fromProps = nextProps.primaryMarker;
            const fromConfigProps = mapCoreConfig.primaryMarker ?? null;
            let iconProps:IMapMarkerIconProps = {
                ...fromConfigProps?.iconProps,
                ...fromProps?.iconProps,
                imagePath: fromProps.iconProps?.imagePath !== undefined ? fromProps.iconProps?.imagePath : fromConfigProps?.iconProps?.imagePath
            }
            iconProps = fromProps?.iconProps && isObjectMatch(fromProps, iconProps) ? fromProps.iconProps  : iconProps;
            let defaultZoomLevel = fromProps.defaultZoomLevel ?? null;
            const fallbackToBoundsZoomLevel= fromProps.fallbackToBoundsZoomLevel ?? (fromConfigProps?.fallbackToBoundsZoomLevel ?? false);
            if(!fallbackToBoundsZoomLevel){
                defaultZoomLevel = defaultZoomLevel ?? (fromConfigProps?.defaultZoomLevel ?? defaultMapZoomLevel);
            }
            primaryMarkerProps = {
                ...fromProps,
                iconProps: iconProps,
                defaultZIndex: fromProps?.defaultZIndex ?? fromConfigProps?.defaultZIndex,
                centreOnClick: fromProps?.centreOnClick ?? (fromConfigProps?.centreOnClick ?? false),
                defaultZoomLevel: defaultZoomLevel
            };
        }

        this.preferredMapFocusOrder = preferredMapFocusOrder;
        this.preferredMapFocus = preferredMapFocus;
        this.definedFocus = definedFocus;
        this.definedFocusBounds = definedFocusBounds;
        this.outerContainerProps = outerContainerProps;
        this.mapProps = mapProps;
        this.userMarkerProps = userMarkerProps;
        this.primaryMarkerProps = primaryMarkerProps;
    }

    requestUserLocation()
    {
        this.userLocation = {
            didRequest: true
        }

        getManager().location.requireUserLocation(true, true).then(() =>
        {
            if(!this.userLocation.didRequest){
                return;
            }
            const position = getManager().location.getLastKnowLngLat();
            const countryCode = position ? (getManager().location.lastKnownUserAddress?.countryCode ?? null) : null;
            this.userLocation.didReceive = true;
            this.userLocation.position = position;
            this.userLocation.countryCode = countryCode ? countryCode : null;
            this.refreshState();
        });
    }


    requestCountryLocation(countryCode:CountryCode)
    {
        this.countryLocation = {
            didRequest: true,
            countryCode: countryCode
        }

        getManager().location.getCountryLocation(countryCode).then((itemAddress: ILocationAddress | null) => {
            if((this.countryLocation.countryCode !== countryCode) || !this.countryLocation.didRequest){
                return;
            }
            this.countryLocation.didReceive = true;
            this.countryLocation.bounds = itemAddress?.lngLatBounds;

            this.refreshState();
        })
    }

    checkForFindActiveFocus(): boolean
    {
        if(this.didFindActiveFocus && this.didFindInitialFocus){
            return false;
        }
        const mapFocus = this.getActiveMapFocus();
        const strictMapFocus = this.getActiveMapFocus(true);
        if(mapFocus !== strictMapFocus){
            return false;
        }
        this.didFindActiveFocus = true;
        this.didFindInitialFocus = true;
        const selectedMarkerFocus = this.getSelectedMarkerFocus();
        if(selectedMarkerFocus && selectedMarkerFocus !== mapFocus){
            this.closePopup();
        }
        if(this.initialStateEstablished && this.mountStateEstablished){
            this.flyToFocus();
        } else {
            this.jumpToFocus();
        }
        return true;
    }

    onMarkerPositionChange(_: MapMarkerInstance)
    {
        this.handleMapChange();
    }

    handleMapChange()
    {
        if(!this.isMapReady){
            return;
        }
        this.handleMapInteraction();
        this.mapChangeCooldown.available().then(() => {
            if(!this.mounted || !this.isMapReady){
                return;
            }
            if(this.props.onMapChange)
            {
                this.props.onMapChange();
            }
        })
    }

    handleMapInteraction()
    {
        if(!this.isMapReady){
            return;
        }
        this.mapInteractionCooldown.available().then(() => {
            if(!this.mounted || !this.isMapReady){
                return;
            }
            if(this.props.onMapInteraction)
            {
                this.props.onMapInteraction();
            }
        })
    }


    onStateEstablished()
    {
        super.onStateEstablished();
        this.checkForFindActiveFocus();
    }

    componentDidMount(){
        super.componentDidMount();
        document.addEventListener("keydown", this.onKeyDown);
    }

    componentWillUnmount(){
        super.componentWillUnmount();
        document.removeEventListener("keydown", this.onKeyDown);
    }

    onKeyDown(event:KeyboardEvent){
        if (event.key === 'Escape') {
            this.onEscapePressed();
        }
    }

    onEscapePressed(){

        if(this.showSelectedMarkerPopup && (this.popupProps?.closeOnEscape ?? (mapCoreConfig.popup?.closeOnEscape) ?? false)){
            this.selectMarker(null);
        }
    }

    getSelectedMarkerFocus():MapFocus|null
    {
        const selectedMarker = this.selectedMarker;
        if(!selectedMarker){
            return null;
        }
        if(selectedMarker === this.getUserMarker()){
            return "user";
        }
        if(selectedMarker === this.getPrimaryMarker()){
            return "primary_marker";
        }
        const markerID = selectedMarker.props.markerID ?? null;
        const cluster = this.getCluster();
        if(cluster && markerID && cluster.clusterObjects[markerID]){
            return "cluster";
        }
        return null;
    }

    resetPrimaryMarkerPosition()
    {
        if (!this.primaryMarkerProps)
        {
            return;
        }

        this.primaryMarkerProps = {
            ...this.primaryMarkerProps,
            marker: {
                ...this.primaryMarkerProps.marker
            }
        }
        this.didFindActiveFocus = false;
        this.refreshState();
    }

    showTransition(){
        if(!this.isMapReady){
            return
        }
        this.transitionKey ++;
        this.transitionReady = false;
        this.refreshState();
    }

    getMap(): MapRef | null
    {
        return this.mapRef.current;
    }

    getMapOuterContainer(): HTMLDivElement | null
    {
        return this.mapOuterContainerRef.current;
    }

    getUserMarker(): MapMarkerInstance | null
    {
        return this.userMarkerRef.current;
    }

    getPrimaryMarker(): MapMarkerInstance | null
    {
        return this.primaryMarkerRef.current;
    }

    getCluster(): MapClusterInstance | null
    {
        return this.clusterRef.current;
    }

    getPopup(): MapPopupInstance | null
    {
        return this.popupRef.current;
    }

    getActiveMapFocus(strict:boolean = false): MapFocus
    {
        const preferredMapFocus = this.preferredMapFocus;
        const preferredOrder = this.preferredMapFocusOrder.slice();

        const index = preferredOrder.indexOf(preferredMapFocus);
        if(index >= 0){
            preferredOrder.splice(index, 1);
            preferredOrder.unshift(preferredMapFocus);
        }
        let validFocus: MapFocus | null = null;
        for (let mapFocus of preferredOrder)
        {
            switch (mapFocus)
            {
                case "defined":
                    validFocus = mapFocus;
                    break;
                case "primary_marker":
                    validFocus = !!this.getPrimaryMarker() || (!strict && !!this.primaryMarkerProps) ? mapFocus : null;
                    break;
                case "user":
                    validFocus = !!this.getUserMarker() || (!strict && (!!this.props.useAssignedUserMarkerPosition || (!!this.userLocation.didRequest && !this.userLocation.didReceive))) ? mapFocus : null;
                    break;
                case "country":
                    validFocus = !!this.countryLocation.bounds || (!strict && !!this.countryLocation.didRequest && !this.countryLocation.didReceive) ? mapFocus : null;
                    break;
                case "cluster":
                    validFocus = !!this.getClusterBounds() ? mapFocus : null;
                    break;
                case "default":
                    validFocus = mapFocus;
                    break;
                default:
                    break;
            }
            if(validFocus){
                break;
            }
        }
        return validFocus ?? "default";
    }

    requirePopupInFocus()
    {
        const markerLngLat = this.selectedMarker?.getLngLat() ?? null;
        const popup = this.getPopup();
        if(!this.selectedMarker?.popupContent || !this.showSelectedMarkerPopup || !markerLngLat || !popup){
            return;
        }

        if(popup.willPopupFit(markerLngLat)){
            return;
        }
        this.flyToFocus();

    }

    flyToFocus(options?: FlyToOptions): void
    {
        const map = this.getMap();
        if(!map){
            return;
        }
        options = {
            ...this.applyFocusToCamera(options)
        }

        const limitFlyToCurve = this.props.limitFlyToCurve ?? (mapCoreConfig.limitFlyToCurve ?? 0);
        if(limitFlyToCurve > 0){
            const currentZoom = map.getZoom();
            const targetZoom = options.zoom ?? currentZoom;
            const targetCentre = ((options.center ?? null) !== null) ? LngLat.convert(options.center!) : null;
            if((targetZoom === currentZoom) && targetCentre){
                const currentCenter = map.getCenter();
                const bounds = map.getBounds();
                const distanceInMetres = getManager().location.getDistanceInMetresBetween(targetCentre, currentCenter);
                const boundsDistanceInMetres = getManager().location.getDistanceInMetresBetween(bounds.getNorthWest(), bounds.getSouthEast());
                if(distanceInMetres < (boundsDistanceInMetres * limitFlyToCurve)){
                    options.curve = options.curve ?? 0;
                }
            }
        }
        map.flyTo(options)
    }

    jumpToFocus(options?: JumpToOptions): void
    {
        this.getMap()?.jumpTo(this.applyFocusToCamera(options))
    }

    applyFocusToCamera(options: CameraOptions = {}): CameraOptions
    {
        const map = this.getMap();
        if (!map)
        {
            return options;
        }

        let position: LngLat;
        let zoomLevel: number;
        const markerLngLat = this.selectedMarker?.getLngLat() ?? null;
        if (markerLngLat)
        {
            position = markerLngLat;
            zoomLevel = map.getZoom()

            let focusOffset:Point|null = null
            if(this.showSelectedMarkerPopup && !!this.selectedMarker?.popupContent){
                focusOffset = this.getPopup()?.focusOffset ?? null;
            }
            if(!focusOffset){
                focusOffset = this.selectedMarker?.focusOffset ?? null
            }

            if(focusOffset){
                position = this.getPositionWithOffset(position, focusOffset)
            }
        } else {
            position = this.getActiveFocusPosition();
            zoomLevel = this.getActiveFocusZoomLevel();
        }

        options = options ?? {};
        options.center = options.center ?? position;
        options.zoom = options.zoom ?? zoomLevel;
        return options;
    }

    getPositionWithOffset(fromPosition:LngLat, offset:Point):LngLat
    {
        const map = this.getMap();
        if(!map){
            return fromPosition;
        }
        const bounds = map.getBounds();
        const width = bounds.getEast() - bounds.getWest();
        const height = bounds.getNorth() - bounds.getSouth();
        let longitude = fromPosition.lng + (width * -offset.x);
        while(longitude <= -180){
            longitude += 180;
        }
        while(longitude > 180){
            longitude -= 180;
        }
        const latitude = Math.max(Math.min(fromPosition.lat + (height * offset.y), 90), -90);
        return new LngLat(longitude, latitude);
    }

    zoomToMarker(marker:MapMarkerInstance, zoomLevel:number|null = null, adjustBy:number = 1): void
    {
        const lngLat = marker.getLngLat();
        const map = this.getMap();
        if(!lngLat || !map){
            return;
        }
        if(zoomLevel === null){
            zoomLevel = map.getZoom() + adjustBy;
        }
        map.flyTo({
            center: lngLat,
            zoom: this.normalizeZoomLevel(zoomLevel)
        });
    }

    getActiveFocusPosition(): LngLat
    {
        return this.getFocusPosition(this.getActiveMapFocus()) ?? new LngLat(0, 0);
    }

    getActiveFocusZoomLevel(): number
    {
        let focus = this.getActiveMapFocus();
        let focusZoomLevel = this.getFocusZoomLevel(focus) ?? 0;

        if (!focusZoomLevel)
        {
            for (let altFocus of this.preferredMapFocusOrder)
            {
                if (altFocus === focus)
                {
                    continue;
                }
                focusZoomLevel = this.getFocusZoomLevel(altFocus) ?? 0;
                if (focusZoomLevel)
                {
                    focus = altFocus;
                    break;
                }
            }
        }
        if (focus === "cluster")
        {

            const cluster = this.getCluster();
            if (cluster)
            {
                const primaryMarkerZoomLevel = this.getFocusZoomLevel("primary_marker");
                const countryZoomLevel = this.getFocusZoomLevel("country");
                const maximumZoomLevel = this.props.cluster?.source?.clusterMaxZoom ?? (mapCoreConfig.cluster?.source?.clusterMaxZoom ?? 20);
                focusZoomLevel = cluster.getActiveFocusZoomLevel(Math.min(focusZoomLevel, maximumZoomLevel), primaryMarkerZoomLevel, countryZoomLevel)
            }
        }
        return focusZoomLevel;
    }

    getFocusPosition(mapFocus: MapFocus): LngLat | null
    {
        let position:LngLat|null = null;
        switch (mapFocus)
        {
            case "primary_marker":
                position = this.getPrimaryMarker()?.getLngLat() ?? null;
                break;
            case "user":
                position = this.getUserMarker()?.getLngLat() ?? null;
                break;
            case "defined":
                if(this.definedFocus && !this.definedFocusBounds){
                    position = this.definedFocus;
                }
                break;
            case "default":
                const latitude = this.mapProps.initialViewState?.latitude ?? null;
                const longitude = this.mapProps.initialViewState?.longitude ?? null;
                if(latitude!== null || longitude !== null){
                    position = new LngLat(longitude ?? 0, latitude ?? 0);
                }
                break;
            default:
                break;
        }
        return position ?? (this.getFocusBounds(mapFocus)?.getCenter() ?? null);
    }

    getFocusBounds(mapFocus: MapFocus): LngLatBounds | null
    {
        switch (mapFocus)
        {
            case "defined":
                return this.definedFocusBounds ?? null;
            case "user":
                return this.getUserMarker()?.appliedBounds() ?? null;
            case "primary_marker":
                return this.getPrimaryMarker()?.appliedBounds() ?? null;
            case "country":
                return this.countryLocation.bounds ?? null;
            case "cluster":
                return this.getClusterBounds();
            default:
                return null;
        }
    }

    getClusterBounds(): LngLatBounds | null
    {
        const cluster = this.getCluster();
        if (!cluster)
        {
            return null;
        }
        const userPosition = this.getFocusPosition("user");
        const primaryMarkerPosition = this.getFocusPosition("primary_marker");
        const countryBounds = this.countryLocation.bounds ?? null;
        return cluster.getClusterBounds(userPosition, primaryMarkerPosition, countryBounds, this.userLocation.countryCode)
    }

    getFocusZoomLevel(mapFocus: MapFocus): number | null
    {
        let zoomLevel:number|null = null;
        if(mapFocus === "user"){
            zoomLevel = this.getUserMarker()?.defaultZoomLevel ?? null;
        } else if(mapFocus === "primary_marker"){
            zoomLevel = this.getPrimaryMarker()?.defaultZoomLevel ?? null;
        }
        if(zoomLevel === null){
            const bounds = this.getFocusBounds(mapFocus);
            zoomLevel = bounds ? this.getZoomLevelForBounds(bounds) : null;
        }
        if(zoomLevel === null){
            if((mapFocus === "default") || ((mapFocus === "defined") && !this.definedFocusBounds)){
                zoomLevel = this.mapProps.initialViewState?.zoom ?? null;
            }
        }
        if(zoomLevel === null){
            return null;
        }
        return this.normalizeZoomLevel(zoomLevel)
    }

    getZoomLevelForBounds(bounds: LngLatBounds, normalize:boolean = false): number | null
    {
        const map = this.getMap();
        if (!map)
        {
            return null;
        }
        let zoomLevel =  map.cameraForBounds(bounds)?.zoom ?? null;
        if(zoomLevel === null || !normalize){
            return zoomLevel;
        }
        return this.normalizeZoomLevel(zoomLevel)
    }

    normalizeZoomLevel(zoomLevel:number):number
    {
        return Math.max(Math.min(Math.floor(zoomLevel), 20), 2)
    }


    onPostRender() {
        super.onPostRender();
        this.determineHaveAllExternalData();
        this.checkForCluster();
        this.checkForInitialFocus();
        this.determineMapReady();
        this.checkForAnimations();
    }

    onMapLoad(e: MapLibreEvent): void
    {
        this.refreshState()
    }

    determineHaveAllExternalData() {
        if(this.haveAllExternalData){
            return;
        }

        this.haveAllExternalData = ((this.props.useAssignedUserMarkerPosition) || (!this.props.userMarker || !!this.userLocation.didReceive)) && (!this.props.countryCode || !!this.countryLocation.didReceive)
    }

    checkForCluster()
    {
        const cluster = this.getCluster();
        if(cluster && !cluster.appliedToMap){
            cluster.applyToMap();
        }
    }

    checkForInitialFocus() {

        if(this.didFindInitialFocus || !this.haveAllExternalData){
            return;
        }
        if(this.checkForFindActiveFocus()){
            this.refreshState();
        }
    }

    determineMapReady() {
        if(this.isMapReady){
            return;
        }

        const cluster = this.getCluster();
        this.isMapReady = !!this.getMap() && this.haveAllExternalData && this.didFindInitialFocus && (!cluster || !cluster.isWaitingForUpdate());
        if(this.isMapReady){
            const enableContextMenu = this.props.enableContextMenu ?? (mapCoreConfig.enableContextMenu ?? false);
            if(enableContextMenu){
                const canvas = this.getMap()?.getCanvas() ?? null
                const container = this.getMap()?.getContainer() ?? null

                canvas?.addEventListener("contextmenu", (event) => {
                    event.stopImmediatePropagation();
                })
                const buttons = container?.getElementsByTagName("button") ?? [];
                for(let button of buttons){
                    button.addEventListener("contextmenu", (event) => {
                        event.stopImmediatePropagation();
                    })
                }
            }

            this.refreshState();
        }
    }

    isStateWaitingForResponse():boolean
    {
        return !this.isMapReady || super.isStateWaitingForResponse();
    }

    onInitialStateEstablished()
    {
        super.onInitialStateEstablished();

        const map = this.getMap();
        if (!map)
        {
            return;
        }

        map.on("zoomend", (_) =>
        {
            const zoom = map.getZoom() ?? 0;
            if (zoom !== this.previousZoom)
            {
                this.previousZoom = zoom;
                this.handleMapChange()
            }
        })
        map.on("move", (_) =>
        {
            const boundsCentre = map.getBounds().getCenter();
            if (this.isNear(boundsCentre, this.previousLngLat))
            {
                return;
            }
            this.previousLngLat.lat = boundsCentre.lat;
            this.previousLngLat.lng = boundsCentre.lng;
            this.handleMapInteraction()
        })

        if (!this.props.allowMapContainerFocusScroll)
        {
            map.getContainer().focus({preventScroll: true});
        }
    }

    isInFocus(focus: MapFocus)
    {
        const activeFocus = this.getActiveMapFocus();
        if (activeFocus === focus)
        {
            return true;
        }

        if (activeFocus === "cluster")
        {
            const cluster = this.getCluster();
            if (cluster)
            {
                return cluster.containsFocus(focus);
            }
        }
        return false;
    }

    isPrimaryMarkerAtDefaultPosition()
    {
        const map = this.getMap();
        const primaryMarker = this.getPrimaryMarker();
        if (!map || !primaryMarker)
        {
            return true;
        }
        const primaryMarkerFocusZoomLevel = this.getFocusZoomLevel("primary_marker");
        return primaryMarker.isAtDefaultPosition() && (map.getZoom() === (primaryMarkerFocusZoomLevel ?? this.getActiveFocusZoomLevel()));
    }

    isMapAtDefaultPosition()
    {
        const map = this.getMap();
        const position = this.getActiveFocusPosition();
        if (!map || !position)
        {
            return true;
        }

        return (this.isNear(position, map.getBounds().getCenter())) && (map.getZoom() === this.getActiveFocusZoomLevel());
    }

    isNear(lngLat1: LngLat, lngLat2: LngLat): boolean
    {
        const map = this.getMap();
        if (!map)
        {
            return false;
        }
        const latProp = (lngLat1.lat - lngLat2.lat) / (map.getBounds().getNorth() - map.getBounds().getSouth());
        const lngProp = (lngLat1.lng - lngLat2.lng) / (map.getBounds().getEast() - map.getBounds().getWest());
        return (latProp < 0.01) && (lngProp < 0.01) && (map.getZoom() === this.getActiveFocusZoomLevel());
    }

    resetMapPosition()
    {
        this.closePopup();
        this.flyToFocus();
    }

    scrollToView()
    {
        getManager().ui.scrollToView("[data-element-id='"+this.elementID+"']");
    }

    selectClusterMarker(markerID: string, objectID: string|null = null, withMove:boolean = false, withPopup:boolean = false, onlyExcludeFromClusterIfNecessary:boolean = false): void
    {
        const cluster = this.getCluster();
        if(!cluster)
        {
            this.selectMarker(null);
            return;
        }

        cluster.excludeMarkerFromCluster(markerID, objectID, onlyExcludeFromClusterIfNecessary).then((marker:MapMarkerInstance|null) => {
            if(!this.mounted){
                return;
            }
            if(!marker){
                this.selectMarker(null);
                return;
            }
            this.selectMarker(marker, withMove, withPopup);
        })
    }

    selectMarker(marker:MapMarkerInstance|null, withMove:boolean = false, withPopup:boolean = false): void
    {
        if(!marker){
            if(this.selectedMarker){
                this.getCluster()?.deselectMarker(this.selectedMarker)
                this.selectedMarker.refreshState();
                this.selectedMarker = null;
            }
            this.showSelectedMarkerPopup = false;
            this.closePopup();
            this.refreshState();
            return;
        }
        if(this.selectedMarker !== marker){
            if(this.selectedMarker){
                this.getCluster()?.deselectMarker(this.selectedMarker)
                this.selectedMarker.refreshState();
            }
            this.selectedMarker = marker;
            this.selectedMarker.refreshState();
        }
        if(withPopup){
            this.openPopup();
        }
        this.whenNextStateEstablished().then(() => {
            if(marker === this.selectedMarker){
                if(withMove){
                    this.flyToFocus();
                } else if(withPopup){
                    this.requirePopupInFocus();
                }
                if(this.props.onMarkerSelected){
                    this.props.onMarkerSelected(marker);
                }
            }
        })
    }

    openPopup(): void
    {
        if(!this.selectedMarker?.popupContent){
            return;
        }
        const popup = this.getPopup();
        this.showSelectedMarkerPopup = true;
        const contentVersion = this.popupProps?.contentVersion ?? 0;
        this.popupProps = {
            ...mapCoreConfig.popup,
            ...this.props.popup,
            ...this.selectedMarker?.popupProps,
            popup: {
                ...mapCoreConfig.popup?.popup,
                ...this.props.popup?.popup,
                ...this.selectedMarker?.popupProps?.popup,
                latitude: 0,
                longitude: 0,
            },
            contentVersion: contentVersion + 1
        }
        this.refreshState();
    }

    onMountStateEstablished()
    {
        super.onMountStateEstablished();
        this.didFindActiveFocus = false;
        this.checkForFindActiveFocus();
    }

    closePopup(): void
    {
        this.getPopup()?.closePopup();
    }

    checkForAnimations() {

        if(this.transitionKey && !this.transitionReady){
            setTimeout(() => {
                this.transitionReady = true;
                this.refreshState()
            })
        }
    }

    renderComponent()
    {

        const parentIsReady = this.isMapReady || !this.props.mapRef || !!this.props.mapRef.current;

        return <>

            <div
                {...this.outerContainerProps}
                data-element-id={this.elementID}
            >
                {parentIsReady && <>

                    <Map
                        {...this.mapProps}
                        ref={this.mapRef}
                        onLoad={this.onMapLoad}
                    >
                        {!!this.props.geolocateControl &&
                            <GeolocateControl
                                {...{
                                    ...mapCoreConfig.geolocateControl,
                                    ...this.props.geolocateControl
                                }}
                            />
                        }

                        {!!this.props.fullscreenControl &&
                            <FullscreenControl
                                {...{
                                    ...mapCoreConfig.fullscreenControl,
                                    ...this.props.fullscreenControl
                                }}
                            />
                        }

                        {(this.props.navigationControl !== null) &&
                            <NavigationControl
                                {...{
                                    ...mapCoreConfig.navigationControl,
                                    ...this.props.navigationControl
                                }}
                            />
                        }

                        {!!this.props.scaleControl &&
                            <ScaleControl
                                {...{
                                    ...mapCoreConfig.scaleControl,
                                    ...this.props.scaleControl
                                }}
                            />
                        }
                        {!!this.props.cluster &&
                            <MapCluster
                                {...this.props.cluster}
                                ref={this.clusterRef}
                                map={this}
                            />
                        }

                        {!!this.userMarkerProps &&
                            <MapMarker
                                {...this.userMarkerProps}
                                map={this}
                                markerType={"user"}
                                ref={this.userMarkerRef}
                            />
                        }
                        {!!this.primaryMarkerProps &&
                            <MapMarker
                                {...this.primaryMarkerProps}
                                map={this}
                                markerType={"primary_marker"}
                                ref={this.primaryMarkerRef}
                            />
                        }

                        {!!this.selectedMarker?.popupContent && this.showSelectedMarkerPopup &&
                            <MapPopup
                                {...(this.popupProps)}
                                ref={this.popupRef}
                                map={this}
                            >
                            </MapPopup>}
                    </Map>
                    {!!this.transitionKey && <>
                        <div key={this.transitionKey} className={"transition-overlay "+(this.transitionReady ? "transition" : "")}></div>
                    </>}
                </>}
            </div>
        </>
    }
}

const MapLibreMap = React.memo(MapLibreMapInstance);
export default MapLibreMap;